import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema } from '../schemas';
import { projectCertificateSchema } from '../../interfaces/IProjectCertificate';

// ============================================================
// Param Schemas
// ============================================================

const projectCertificateParamsSchema = z.object({
	projectId: requiredStringSchema,
	certificateId: requiredStringSchema,
});

// ============================================================
// Body Schemas
// ============================================================

/**
 * The certificate fields a client may set. The project is NOT one of them: the
 * URI carries it, and that is the value the membership guard verified.
 */
const certificateDetailsBodySchema = z.object({
	name: requiredStringSchema,
	groupName: requiredStringSchema,
	isCommonlyUsed: z.boolean(),
});

/**
 * `projectId` is absent by construction. `projectCertificateSchema.partial()`
 * used to allow it, and while `scopedIdFilter` stopped a caller READING a
 * foreign certificate, nothing stripped the field from the `$set` — so an
 * authorized-project update could relocate a certificate into another project.
 * Omitting it from the schema is the fix.
 */
const updateCertificateBodySchema = projectCertificateSchema.omit({ _id: true, projectId: true }).partial();

const deleteMultipleCertificatesBodySchema = z.object({
	ids: z.array(z.string()).min(1),
});

// ============================================================
// Response Schemas
// ============================================================

const deletedCountResponseSchema = z.object({
	deleted: z.number(),
});

// ============================================================
// Contract
// ============================================================

/**
 * Certificates are project data: `/projects/:projectId/certificates`.
 *
 * `{ root: true }` — every route here is project-scoped, so nothing in this file
 * is left behind by dropping the `/certificates` prefix.
 *
 * IMPORTANT: literal sub-paths (`/multiple`) must be declared BEFORE the
 * parameterized `/:certificateId`.
 */
export const certificateContract = defineContract(
	'certificates',
	{
		getAllCertificates: route({
			method: 'GET',
			path: '/projects/:projectId/certificates',
			params: projectIdParamsSchema,
			response: z.array(projectCertificateSchema),
			operationId: 'getAllCertificates',
			tags: ['Certificates'],
			summary: 'List the certificates of a project',
		}),
		createCertificate: route({
			method: 'POST',
			path: '/projects/:projectId/certificates',
			params: projectIdParamsSchema,
			body: certificateDetailsBodySchema,
			response: projectCertificateSchema,
			operationId: 'createCertificate',
			tags: ['Certificates'],
			summary: 'Create a certificate in a project',
		}),
		createMultipleCertificates: route({
			method: 'POST',
			path: '/projects/:projectId/certificates/multiple',
			params: projectIdParamsSchema,
			body: z.array(certificateDetailsBodySchema).min(1),
			response: z.array(projectCertificateSchema),
			operationId: 'createMultipleCertificates',
			tags: ['Certificates'],
			summary: 'Create several certificates in a project',
		}),
		/**
		 * Bulk delete. Foreign ids are a no-op, not an error: the scoped filter
		 * matches nothing and the response reports how many rows actually went,
		 * which is Issues' `reassignIssues` precedent (`200 {updated: 0}`). A 404
		 * here would leak "none of these ids exist in your project".
		 */
		deleteMultipleCertificates: route({
			method: 'DELETE',
			path: '/projects/:projectId/certificates/multiple',
			params: projectIdParamsSchema,
			body: deleteMultipleCertificatesBodySchema,
			response: deletedCountResponseSchema,
			operationId: 'deleteMultipleCertificates',
			tags: ['Certificates'],
			summary: 'Delete several certificates of a project by id',
		}),
		getCertificateById: route({
			method: 'GET',
			path: '/projects/:projectId/certificates/:certificateId',
			params: projectCertificateParamsSchema,
			response: projectCertificateSchema,
			operationId: 'getCertificateById',
			tags: ['Certificates'],
			summary: 'Get a certificate of a project by id',
		}),
		updateCertificate: route({
			method: 'PUT',
			path: '/projects/:projectId/certificates/:certificateId',
			params: projectCertificateParamsSchema,
			body: updateCertificateBodySchema,
			response: projectCertificateSchema,
			operationId: 'updateCertificate',
			tags: ['Certificates'],
			summary: 'Update a certificate of a project',
		}),
		deleteCertificate: route({
			method: 'DELETE',
			path: '/projects/:projectId/certificates/:certificateId',
			params: projectCertificateParamsSchema,
			response: z.string(),
			operationId: 'deleteCertificate',
			tags: ['Certificates'],
			summary: 'Delete a certificate of a project',
		}),
	},
	{ root: true }
);
